import { SortDirection } from '@angular/material/sort';
import * as L from 'leaflet';

import {
  CurrencyType,
  ECustomIcons,
  ELicenseExpirationStatus,
  ELicenseValidationStatus,
  EManifestItemType,
  EManifestStatuses,
  EModuleExtIDs,
  EModuleIDs,
  EModuleURLs,
  EOrderPaymentType,
  EOrderSettingTypes,
  EOrderStatuses,
  EOrderTypes,
  ERoles,
  ESafetyInspectionFrequency,
  ESafetyInspectionStatus,
  ETextClasses,
  MeasurementSystemType,
} from 'app/consts/enums';
import { Moment } from 'moment';
import { from } from 'rxjs';
import { DateTimeService } from 'app/services/date-time.service';
import { ILiveMapManifest } from 'app/modules/live-map/types';
import { IEditableOrderItem } from 'app/main/content/orders/types';

/** Expects string of YYYY-MM-DDTHH:mm:ss.SSSZ format */
export type timeStamp = string;
/** Expects string of HH:mm or HH:mm:ss format */
export type timeString = string;
export type numberBoolean = 0 | 1;

export type IOrderAny = IOrder | IOrderNode | IOrderFull;

export type IModuleRoute = Exclude<EModuleIDs, EModuleIDs.OrdersAllocation>;

export interface IModule {
  idx: number;
  id: EModuleIDs | EModuleExtIDs;
  title: string;
  type: 'group' | 'button' | 'collapse';
  icon: string;
  url: EModuleURLs;
  visible: boolean;
  readonly: boolean;
  /** For group items */
  children?: Array<IModule>;
  // unsupported properties
  /** deprecated */
  show_badge?: numberBoolean;
  /** deprecated */
  translate?: string;
}

interface IAccountInfo {
  FullName: string;
  ABN: string;
  webSite: string;
  Address: string;
  GST: number;
  phoneNr: string;
  NotificationPhone: string;
  Timezone?: string;
  currency?: CurrencyType;
  measurementSystem?: MeasurementSystemType;
  fax: string;
}

export interface ITimezone {
  Id: string;
  DisplayName: string;
}

export interface IGeneralSettings extends IAccountInfo {
  SenderName: string;
  hideLegacyReports: boolean;
  showOldLiveMap: boolean;
}

export interface IAccountParent {
  Id: number;
  Name: string;
}

export interface IDeviceOptionsSettings {
  showCashReceived?: boolean;
  pickupSignaturesRequired?: boolean;
  deliverySignaturesRequired?: boolean;
  deliveryItemCompletionRequired?: boolean;
  pickupItemCompletionRequired?: boolean;
  pickupPhotosRequired?: boolean;
  deliveryPhotosRequired?: boolean;
  pickupLoadsRequired?: boolean;
  deliveryLoadsRequired?: boolean;
  pickupCommentsRequired?: boolean;
  deliveryCommentsRequired?: boolean;
  deliveryUploadPhotosFromLibrary?: boolean;
  pickupUploadPhotosFromLibrary?: boolean;
  retainPhotosOnDevice?: boolean;
}

export interface IOrdersSettings {
  hide_orders_days: number;
  ignoreTimeForOrders: boolean;
  showOrderDeliveryCost: boolean;
  showOrderItemCost: boolean;
  showOrderItemDimensions: boolean;
  showOrderCapacity: boolean;
  retainImportedAddresses: boolean;
  geocodeWithName: boolean;
  useSystemDefaultTimes: boolean;

  use_cust_ref_in_pdf: boolean;
  generatePDFManifestSummaries: boolean;
  importZonalManifests: boolean;
  manualZonalManifests: boolean;
  vehicleStartsEmpty: boolean;
  zonalManifestDateLookahead: boolean;
  clearUnallocatedOrderDates: boolean;
  highlightWords: string;

  defaultDeliveryTime: timeString;
  /** in minutes */
  defaultDeliveryTolerance: number;
  /** in minutes */
  defaultDeliveryServiceTime: number;

  defaultPickupTime: timeString;
  /** in minutes */
  defaultPickupTolerance: number;
  /** in minutes */
  defaultPickupServiceTime: number;
  manifestRealTimeRoutesEnabled: { enabled: boolean; defaultValue: boolean };
  defaultOrderType: number;
  DuplicateOrderCustomerReference: boolean;
  allowDriverToUpdateOrderLocation: boolean;
  updateCustomerAddress: boolean;

  ManifestDefaultStart: timeString;
  ManifestDefaultStop: timeString;
}

export interface INotificationOptionsSettings {
  pickupEnrouteSmsNotificationEnabled?: boolean;
  pickupEnrouteEmailNotificationEnabled?: boolean;
  pickupCompletedSmsNotificationEnabled?: boolean;
  pickupCompletedEmailNotificationEnabled?: boolean;
  deliveryEnrouteSmsNotificationEnabled?: boolean;
  deliveryEnrouteEmailNotificationEnabled?: boolean;
  deliveryCompletedSmsNotificationEnabled?: boolean;
  deliveryCompletedEmailNotificationEnabled?: boolean;
}

type IConfigBasic = IOrdersSettings &
  IDeviceOptionsSettings & {
    APIKEY: string;
    enable_units_tracking: boolean;
    enableBarCodeTracking: boolean;
    attributes: Array<IAttribute>;
    capacityType: ICapacityType;
  };

export interface IAccountStats {
  Country: string;
  Industry: string;
  MonthlyOrders: string;
  VehicleCount: string;
  Platform: string;
}

interface ICapacityTypeBasic {
  Name: string;
  Unit: string;
  Units: string;
}

export interface ICapacityType extends ICapacityTypeBasic {
  Id: number;
}

export interface IMapServer {
  Id: number;
  Name: string;
  Description: string;
}

export interface IConsignmentNote {
  Id: number;
  Name: string;
}
export interface IAppData extends IConfigBasic {
  accountId: number;
  zones: Array<IZone>;
  unitsOfmeasure: Array<ILoadType>;
  unitsOfmeasureStatuses: Array<ILoadOption>;
  orderTypes: Array<IOrderType>;
  orderStatuses: Array<IOrderStatus>;
  //#region Integrations
  hasJiwaLinked: boolean;
  hasMyObLinked: boolean;
  hasOrderMentumLinked: boolean;
  hasShopifyLinked: boolean;
  hasNetSuiteLinked: boolean;
  hasWooCommerceLinked: boolean;
  hasMixLinked: boolean;
  hasFreshoLinked: boolean;
  hasLinxioLinked: boolean;
  DisableOptimisation: boolean;
  //#endregion Integrations
}

export interface IAppConfig extends IAppData {
  orderTypesMap: Record<EOrderTypes, string>;
  orderStatusesMap: Record<EOrderStatuses, string>;
  manifestStatusesMap: Record<EManifestStatuses, IManifestStatusInfo>;
  accountAttributes: Array<IAttribute>;
  loadTypes: Array<ILoadType>;
  loadOptions: Array<ILoadOption>;
}

interface IOrderStatus {
  id: EOrderStatuses;
  name: string;
}

interface IOrderType {
  id: EOrderTypes;
  name: string;
}

export type IAccountConfig = IConfigBasic &
  IAccountStats &
  IGeneralSettings &
  INotificationOptionsSettings & {
    id: number;
    /** Load Types */
    units_of_measure: Array<ILoadType>;
    routeopt: IRouteOptimizationOptions;
    GMPRO: {
      isClusteringEnabled?: boolean;
      routeClusteringRadiusType?: number | null; // 1, 2, 3, 4, or NULL (defaults to 1 if NULL)
      routeClusteringRadiusValue?: number | null; // NULL or positive integer (only relevant if Type == 4)
      isTollsAvoidanceEnabled?: boolean;
      isHighwaysAvoidanceEnabled?: boolean;
      isFerriesAvoidanceEnabled?: boolean;
      isBreakRulesEnabled?: boolean;
      breakType?: number;
      breakDuration?: number;
      breakAfterWorkedHours?: number;
      breakStartTime?: timeString;
      isRoutePreferencesEnabled?: boolean;
      routeObjectiveType?: string;
    };
    depo_address?: string;
    depo_lat?: number;
    depo_lng?: number;
    //#region Integrations
    jiwa_key: string;
    Mix?: IIntegrationMix;
    WooCommerce?: IIntegrationWooCommerce;
    NetSuite?: IIntegrationNetSuite;
    OrderMentum?: IIntegrationOrderMentum;
    Shopify?: IIntegrationShopify;
    fresho?: IIntegrationFresho;
    Linxio?: IIntegrationLinxio;
    //#endregion Integrations
    hideNotificationTab: boolean;
    validLicenseIsMandatory: boolean;
    mapServerId?: number;
    OptimisationStrategy?: number;
    ReturnToDepot?: boolean;
    capacityTypeId?: number;
    SpeedFactor?: number | null;
    DisableOptimisation: boolean;
  };

export interface IAccountConfigFull extends IAccountConfig {
  name: string;
}

interface IIntegrationBase {
  Id?: number;
  AccountId?: number;
}

export interface INeoNetworksIntegration {
  Id: number;
  AccountId: number;
  IntegrationTypeId: number;
  IntegrationTypeName: string;
  DateActivated: string;
  IsActive: boolean;
}

export interface IIntegrationMix extends IIntegrationBase {
  IdentityServerBaseURL: string;
  ClientId: string;
  ClientSecret: string;
  UserName: string;
  Password: string;
  Scopes: string;
  APIBaseURL: string;
  GroupId: string;
  AllowVehicleImport: boolean;
  AllowDriverImport: boolean;
}

export interface IVehicleFullDto {
  id: number;
  description: string;
  disabled: number;
  maxVolume?: number;
  maxLength?: number;
  PrimeMover: boolean;
  VIN: string;
  QRIdentity: string;
  DistanceRate: number;
  AssetId: string;
  name: string;
  attributes: number[];
  groups: number[];
  /** Speed factor for route optimisation. Empty/null = use Account Speed Factor. */
  speedFactor?: number | null;
}

export interface IIntegrationWooCommerce extends IIntegrationBase {
  BaseURL: string;
  ClientKey: string;
  ClientSecret: string;
  StatusFilter: string;
}

export interface IIntegrationNetSuite extends IIntegrationBase {
  DeliveredEndPoint: string;
  ReturnEndPoint: string;
  NSSource: string;
}

export interface IIntegrationOrderMentum extends IIntegrationBase {
  BaseURL: string;
  UserName: string;
  Password: string;
  ContainerType: string;
  PackSize: number;
  DeliveryDateOffset: number;
  ManifestFilter: string;
  WebhookId: string;
  SupplierId: string;
  IsWebhookEnabled: boolean;
  ImportToManifest: boolean;
  IsPushbackEnabled: boolean;
  PropertyTemplate: string;
  DoNotImportList?: string;
  DoNotAllocateList?: string;
  DoNotOverrideCustomerPhoneAndEmail: boolean;
}

export interface IIntegrationShopify extends IIntegrationBase {
  AccountName: string;
  Key: string;
  SecretKey: string;
  APIVersion: string;
}

export interface IIntegrationFresho extends IIntegrationBase {
  userName: string;
  password: string;
  folder?: string;
  invoiceFolder?: string;
  autorun?: boolean;
  isExportEnabled?: boolean;
  exportFolder?: string;
}

export interface IIntegrationLinxio extends IIntegrationBase {
  UserEmail: string;
  UserPassword: string;
}

interface ILoad {
  id: number;
  qty: number;
}

export interface ILoadFull extends ILoad {
  name: string;
  /** Load option ID */
  stat_id?: number;
}

export interface ILoadType extends ILoadFull {
  show_on_tables: numberBoolean;
}

export interface IReturnItemsReason {
  id: number;
  reason: string;
}

export interface IReturnItemsReasonDialogData {
  currentReason?: IReturnItemsReason;
  allReasons: IReturnItemsReason[];
}

export interface ILoadOption {
  id: number;
  name: string;
  calc: string;
  /** Order Type ID - string version of key from EOrderTypes | EOrderDefaultLoadOption */
  orders: string;
}

export interface IAttribute {
  id: number;
  Name: string;
}

export interface IZone {
  id: number;
  name: string;
  /** Stringified array of coords arrays ([lat, lng]) */
  zone_polygon: string;
}

export interface IZonePolygon {
  zoneID: number;
  polygon: L.Polygon | null;
}

export interface IRouteOptimizationOptions {
  breaksEnabled: boolean;
  shift_length: number;
  break_length: number;
  use_client_name_in_opt_results: boolean;
  dont_send_orders_on_auto_alloc_submit: boolean;
  dyn_alloc_max_calc: number;
  dyn_alloc_wait_time: number;
  dyn_alloc_max_vehicle_time: number;
  stop_on_first_manifest: boolean;
  hide_fault_results: numberBoolean;
}

export interface ISpeedFactorOption {
  id: number;
  code: string;
  factor: number | null;
}

export interface ITimetable {
  id: number;
  name: string;
  IsDefault?: 0 | 1;
}

export interface ITimetableFull extends ITimetable {
  records: Array<ITimetableRecord>;
}

export interface ITimetableRecord {
  id: number;
  WeekDay: number;
  StartTime: string;
  EndTime: string;
}

export interface IDriverNew {
  AccountId: number;
  FullName: string;
  PhoneNumber?: string;
  UserLogin: string;
  UserPassword: string;
  StartAtDepot: boolean;
  FinishAtDepot: boolean;
  StartLocationName?: string;
  StartLocationLat?: number;
  StartLocationLng?: number;
  FinishLocationName?: string;
  FinishLocationLat?: number;
  FinishLocationLng?: number;
}

export interface IDriver {
  id: number;
  FullName: string;
  UserName?: string;
  VehicleId: number;
  VehicleName: string;
  tt_name: string;
  atrib_string: string;
  DeviceInfo?: string;
  AppVersion?: string;
  LastHeartbeat?: timeStamp;
  phone_nr: string;
  start_at_depot: boolean;
  finish_at_depot: boolean;
  availability: Array<{
    AvailableAllDays: boolean;
    Day?: number;
    StartTime: string;
    FinishTime: string;
  }>;
  validLicenseIsMandatory: boolean;
  license?: IDriverLicense;
  timezone?: string;
}

export interface IDriverFull extends IDriver {
  smartmove_access?: boolean;
  prestart_access?: boolean;
  finish_at_depot: boolean;
  AssignedTimeTable: number;
  pin: string;
  start_location_name: string;
  start_location_lat: number;
  start_location_lng: number;
  finish_location_name: string;
  finish_location_lat: number;
  finish_location_lng: number;
  breaks: Array<IDriverBreak>;
  zones: Array<number>;
  attributes: Array<string>;
  UserLink?: number;
  UserId?: number;
  UserPassword?: string;
  DriverId?: number;
  IsDeleted?: boolean;
  HourlyRate?: number;
}

export interface IDriverBreak {
  break_start: string;
  break_allowance: number;
  duration: number;
  force_break: numberBoolean;
}

export interface IDriverLicense {
  id: number;
  sideAPhotoId: number;
  sideBPhotoId: number;
  validTillDate: timeStamp;
  driverId: number;
  validationStatus: ELicenseValidationStatus;
  expirationStatus: ELicenseExpirationStatus;
  uploadDate: timeStamp;
  sideAPhotoUrl: string;
  sideBPhotoUrl: string;
}

export interface IStatusInfo {
  icon: ECustomIcons;
  color: ETextClasses;
  text: string;
}

export interface IVehicleBasic {
  id: number;
  name: string;
}

export interface IVehicleGroupNew {
  Name: string;
  AccountId: number;
  Vehicles: Array<number>;
}

export type IVehicleGroup = Omit<IVehicleGroupNew, 'Vehicles'> & {
  Id: number;
};

export enum CrucialInspectionStatus {
  Overdue = 1,
  Fail = 2,
  CriticalFail = 3,
}

export type CrucialInspectionInfo = {
  IsMandatory: boolean;
  Status: CrucialInspectionStatus;
  PlanId: number;
  PlanName: string;
};

export interface IVehicle extends IVehicleBasic {
  Id?: number;
  objectno?: string;
  LastOnline?: timeStamp;
  disabled: numberBoolean;
  capacityName?: string;
  capacityUnit?: string;
  capacityUnits?: string;
  attributes?: Array<number>;
  groups?: Array<number>;
  maxWeight?: number;
  maxVolume?: number;
  maxLength?: number;
  /** Vehicle latitude */
  lat?: number;
  /** Vehicle longitude */
  lng?: number;
  QRIdentity?: string;
  VehicleDeepLink: string;
  HasMoved?: boolean;
  IsDeleted?: boolean;
  AssetId?: string;
  Plans?: ISafetyPlanToVehicles[];
  RecentCrucialInspection?: CrucialInspectionInfo;
}

export interface IVehicleFull extends IVehicle {
  VIN: string;
  description: string;
  DeviceSN: string;
  app_ver: string;
  ignition: numberBoolean;
  orders: Array<IOrderForVehicle>;
  manufacturer: string;
  manufact_year: number;
  DistanceRate?: number;
}

export interface IVehicleCombined {
  vehicle: IVehicleFull;
  capacity: ICapacityTypeBasic;
}

export interface IVehicleDeleteOrderResponse {
  orders: Array<IOrderForVehicle>;
  status: string;
}

export type IOrderForVehicle = IOrderBasic &
  IOrderAdditionalInfo & {
    DateCreated: timeStamp;
    // TO DO should be added to model [SB-150]
    manifest_id: number;
    // TO DO should be added to model [SB-150]
    manifest_name: string;
  };

export interface ICustomer {
  id: number;
  client_number: string;
  name: string;
  Address: string;
  /** Address latitude */
  lat: number;
  /** Address longitude */
  lng: number;
  defaultDeliveryDetails: string;
  /** Notification emails - string with items separated by ';' */
  not_emails?: string;
  /** Notification phones - string with items separated by ';' */
  not_sms?: string;
  defaultDeliveryTime?: timeString;
  defaultDeliveryTolerance?: number;
  defaultServiceTime?: number;
  CustomerAddress?: string;
  CustomerAddressLat?: number;
  CustomerAddressLng?: number;
}

export interface ICustomerFull extends ICustomer {
  /** emails - string with items separated by ';' */
  PhoneNr: string;
  /** phones - string with items separated by ';' */
  Email: string;
  enableSMS: boolean;
  zone: number;
  Details: string;
  ContactPersonFirstName: string;
  ContactPersonLastName: string;
  ContactPersonPhoneNr: string;
  ContactPersonEmail: string;
  default_delivery_time: timeString;
  /** in minutes */
  default_delivery_tolerance: number;
  /** in minutes */
  default_service_time: number;
  /** Loads */
  units: Array<ILoad>;
  unitsChangedReason?: string;
  Timezone?: string;
  KeepExistingAddress: boolean;
}

export interface ICustomerSearchAnswer {
  list: Array<ICustomer>;
  tot_records: number;
}

export interface IUnitLogRecord {
  comment: string;
  dt: timeStamp;
  id: number;
  inboundClientQty: number;
  order_id: number;
  order_type: number;
  qtyChanged: number;
  unit_name: string;
  user_id: number;
  user_name: string;
}

export interface IUnitAdjustmentData {
  units: Array<ILoadFull>;
  unitsChangedReason?: string;
}

export interface IShowOnMapData {
  title: string;
  markers?: Array<L.Marker>;
  manifests?: Array<IManifestFull>;
  currentAddress?: string;
  currentLat?: number;
  currentLng?: number;
}

interface IOrderBasic {
  id: number;
  StatusId: EOrderStatuses;
  /** EOrderTypes for real orders; 0 = break, -1 = start, -2 = end (manifest sentinel rows) */
  TypeId: EOrderTypes | 0 | -1 | -2;
}

interface IOrderCommon extends IOrderBasic {
  client_name?: string;

  driver_id?: number;
  driver_name?: string;

  // delivery
  dest_lat?: number;
  dest_lng?: number;
  dest_address?: string;

  // pickup
  pickup_lat?: number;
  pickup_lng?: number;
  pickup_address?: string;

  /** number of driver comments */
  has_comments?: number;
  has_pictures?: boolean;
  has_returns?: number;

  weight?: number;
  volume?: number;
  Length?: number;
  ShipmentId?: number;
  pickup_client_name?: string;
  delivery_client_name?: string;
  AllPickupClientNames?: string;
  AllDeliveryClientNames?: string;
  AllCustomerReferences?: string;
}

interface IOrderDate {
  /** Order date - seems calculated from pickup or delivery */
  dt?: timeStamp;
}

interface IOrderVehicle {
  vehicle_id?: number;
  vehicle_name?: string;
}

interface IOrderSizing {
  /** Maximum of Length / Width / Height */
  order_max_dimension?: number;

  /** In general this is total weight for the order, but it depends on the capacity type for the account */
  order_total_capacity?: number;

  /** Capacity type name along with the units (singular and plural) */
  capacity_name?: string;
  capacity_unit?: string;
  capacity_units?: string;
}

export type IOrder = IOrderCommon &
  IOrderDate &
  IOrderVehicle &
  IOrderSizing & {
    cust_ref?: string;
    /** Manifest ID */
    m_id?: number;
    /** Manifest name */
    m_name?: string;

    delivery_client_id?: number;
    // Delivery timing properties
    /** Delivery tolerance */
    dtol?: number;
    /** Planned delivery date */
    ddt?: timeStamp;
    /** Actual delivery date */
    addt?: timeStamp;

    pickup_client_id?: number;
    // Pickup timing properties
    /** Pickup tolerance */
    ptol?: number;
    /** Planned pickup date */
    pdt?: timeStamp;
    /** Actual pickup date */
    apdt?: timeStamp;

    pickup_eta?: timeStamp;

    delivery_eta?: timeStamp;

    attributes?: string;
    orderAttributes?: Array<number>;

    /**
     * Load values separated by '/' (without name or id)
     * @deprecated at least seems so
     */
    units?: string;
  };

export interface IOrderListModelPagination extends IOrder {
  TotalCount?: number;
  OrderTypeName?: string;
  OrderStatusName?: string;
  ETA?: string;
  AvailableWindow?: string;
  OrderRefOrId?: string;
  numberOfOrder?: number;
  customerReference?: string;
}

export interface IShipmentOrderListModelPagination extends IOrderListModelPagination {
  deliveryCustomers?: ShipmentCustomerInfo[];
  pickupCustomers?: ShipmentCustomerInfo[];
  orders?: IOrderListModelPagination[];
}

export interface ShipmentCustomerInfo {
  id: number;
  name?: string;
}

export interface IOrderListResponse {
  len: number;
  list: Array<IOrderListModelPagination>;
}

export interface IOrdersToManifestRequest {
  manifest_id: number;
  order_ids: Array<number>;
}

export interface IOrdersResponse {
  len: number;
  list: Array<IOrder>;
}

export interface IOrderResponse {
  orderDetails: IOrderFull;
  itemsLists: Array<null>;
  vehicleId?: number;
  vehicleLat_pickup?: number;
  vehicleLng_pickup?: number;
  vehicleLat_delivery?: number;
  vehicleLng_delivery?: number;
}

interface IOrderNodesExtendedInfo {
  // delivery
  DeliveryClientID?: number;
  delivery_client_name?: string;
  DeliveryTime?: timeStamp;
  DeliveryTolerance?: number;
  DeliveredTime?: timeStamp;
  DeliveryServiceTime?: number;

  // pickup
  PickupClientID?: number;
  pickup_client_name?: string;
  PickupTime?: timeStamp;
  PickupTolerance?: number;
  PickedUpTime?: timeStamp;
  PickupServiceTime?: number;
}

interface IOrderAdditionalInfo {
  CustomerReference?: string;
  // TO DO 'delivery_price' should be moved to IOrderCommon after backend update
  delivery_price?: number;
}

export interface IOrderPhoto {
  orderId: number;
  pictureId: number;
  pictureUrl: string;
  accuracy: any;
  latitude: number;
  longitude: number;
  thumbnailPictureId: number;
  thumbnailPictureUrl: string;
  createDate: string;
  uploadDate: string;
  typeId: number;
}

export type IOrderFull = IOrderCommon &
  IOrderNodesExtendedInfo &
  IOrderAdditionalInfo &
  IOrderSizing & {
    PayerID?: number;
    payer_name?: string;
    /** Capacity */
    OrderDetails?: string;
    orderAttributes?: Array<number>;
    /** Loads */
    units?: Array<ILoadFull>;
    orderBarcodes: IBarcode[];

    // delivery
    /** Delivery notification emails - string with items separated by ';' */
    delivery_not_emails?: string;
    /** Delivery notification phones - string with items separated by ';' */
    delivery_not_sms?: string;
    DeliveryServiceTime?: number;
    delivery_signature_lat?: number;
    delivery_signature_lng?: number;
    DeliverySignedBy?: string;
    DeliveryComment?: string;
    DeliveryStartedAt?: timeStamp;
    DeliveryFinishedAt?: timeStamp;
    /** tracking link id - delivery */
    DeliveryKey?: string;

    CancellationComment?: string;
    CancelledAt?: timeStamp;

    // pickup
    /** Pickup notification emails - string with items separated by ';' */
    pickup_not_emails?: string;
    /** Pickup notification phones - string with items separated by ';' */
    pickup_not_sms?: string;
    PickupServiceTime?: number;
    pickup_signature_lat?: number;
    pickup_signature_lng?: number;
    PickupSignedBy?: string;
    PickupComment?: string;
    PickupStartedAt?: timeStamp;
    PIckupFinishedAt?: timeStamp;
    /** tracking link id - pickup */
    PickupKey?: string;

    /** date format YYYY_MM_DD_HH_mm_ss */
    pictures?: Array<string>;
    photos?: Array<IOrderPhoto>;
    orderItems?: Array<IOrderItem>;
    inventoryItems?: Array<IInventoryItem>;
    amount_payed?: number;
    payment_type?: EOrderPaymentType;
    money_received: boolean;

    invoiceLink: string;
    orderSettings: IOrderSetting[];

    manifest_id?: number;
    manifest_name?: string;
    TopPriority?: boolean;

    DeliveryTrackingLink?: string;
    PickupTrackingLink?: string;
    numberOfOrder?: number;
  };

export interface IOrderItem {
  id: number;
  orderId: number;
  accountId: number;
  reference: string;
  product: string;
  customDescription?: string;
  itemPrice: number;
  quantity: number;
  itemLength: number;
  itemWidth: number;
  itemHeight: number;
  itemWeight: number;
  totalVolume: number;
  totalWeight: number;
  returnQuantity: number;
  unit: string;
  extendedPrice: number;
  itemVolume: number;
  returnItemsReasonDescription: string;
}

export interface IInventoryItem {
  Id: number;
  Reference: string;
  Product: string;
  CustomDescription: string;
  ItemPrice: number;
  ItemLength: number;
  ItemWidth: number;
  ItemHeight: number;
  ItemWeight: number;
  AccountId: number;
  UnitOfMeasure: string;
}
export interface IOrdersGetParameters {
  //#region Status
  loadPendingOrders: boolean;
  loadDeliveringOrders: boolean;
  loadDeliveredOrders: boolean;
  loadCancelledOrders: boolean;
  //#endregion Status

  //#region Types
  loadDeliveryOrders: boolean;
  loadPickupOrders: boolean;
  loadServiceOrders: boolean;
  //#endregion Types

  //#region Dates
  loadOrdersBetweenDates: boolean;
  loadOrdersStartDate: timeStamp;
  loadOrdersEndDate: timeStamp;
  //  loadOrdersModifiedAfter: timeStamp;

  // TO DO maybe delete [SB-183]
  onlyShowTodaysOrders?: boolean;
  //#endregion Dates

  //#region Other
  /** exclude allocated orders */
  excludeManifestOrders: boolean;
  //#endregion Other

  start?: number;
  len?: number;
  sortColumn?: string;
  sortDirection?: string;
  zones?: Array<number>;
  searchText?: string;
}

export interface ILiveMapOrdersParams {
  dateFrom: timeString;
  dateTo: timeString;
  orderTypeIds: number[];
  hideOrdersWithoutDate: boolean;
}

export interface IOrderDashboardParams {
  dateFrom: Date;
  dateTo: Date;
}

export interface IOrderDashboardModel {
  totalCustomers: number;
  totalEntities: number;
  totalEntitiesCompleted: number;
  totalEntitiesFutiled: number;
  avgServiceTime: timeString;
  avgManifestTime: timeString;
}

export interface ILiveMapManifestsParams {
  dateFrom: timeString;
  dateTo: timeString;
  ids?: number[];
}

export interface IOrderDuplicate {
  id: number;
  StatusId: EOrderStatuses;
  TypeId: EOrderTypes;
  dest_address?: string;
  dest_lat?: number;
  dest_lng?: number;
  pickup_address?: string;
  pickup_lat?: number;
  pickup_lng?: number;
  PickupTolerance?: number;
  PickupServiceTime?: number;
  OrderDetails?: string;
  DeliveryTolerance?: number;
  DeliveryServiceTime?: number;
  PayerID?: number;
  payer_name?: string;
  delivery_price?: number;
  delivery_not_emails?: string;
  delivery_not_sms?: string;
  pickup_not_emails?: string;
  pickup_not_sms?: string;
  DeliveryClientID?: number;
  PickupClientID?: number;
  volume?: number;
  Length?: number;
  weight?: number;
  orderAttributes?: Array<number>;
  orderSettings: IOrderSetting[];
  orderItems?: Array<IOrderItem>;
  delivery_client_name?: string;
  pickup_client_name?: string;
  TopPriority?: boolean;
  PickupTime?: timeStamp;
  DeliveryTime?: timeStamp;
}

export interface IManifest {
  id: number;
  name: string;
  vehicleId: number;
  VehicleName: string;
  driverId: number;
  driver_name: string;
  ManifestDate: timeStamp;
  OrdersQty?: number;
  OrdersCompletedOrCancelledCount?: number;
  OrdersInProgressCount?: number;
  OrdersRemaining?: number;
  /** No idea what it is */
  keepCompleted?: boolean;
  /** No idea what it is */
  newOrderAllocated?: boolean;
  forceUpdateDates?: boolean;
  linkedDriver?: IDriver;
  linkedVehicle?: IVehicle;
  TotalDeliveryAmount: number;
  Start: Date;
  Finish: Date;
  OptimisationAccepted?: boolean;

  CapacityName?: string;
  CapacityUnit?: string;
  CapacityUnits?: string;
  TotalCapacity?: number;
  VehicleCapacity?: number;
  VehicleLoadLength?: number;
  MaxDimension?: number;
  ManifestAttributes?: string;
  ManifestAttributeIds?: Array<number>;
  RouteCost?: number;
  RouteDistance?: number;
  RouteMinutes?: number;
  ActualRouteCost?: number;
  ActualRouteDistance?: number;
  ActualRouteMinutes?: number;
  has_returns?: number;
  realTimeRouteVisible?: boolean;
  EarliestStart?: timeStamp;
  LatestFinish?: timeStamp;
  CompleteStatus?: EManifestStatuses;
}

export interface IManifestToVehicleResponse {
  transmit_results: {
    status: 0 | -1;
    statustext: string;
  };
}

export interface IManifestDuplicateRequest {
  manifest_ids: Array<number>;
  /** Date for duplicates */
  dt: Date;
}

export interface IManifestSplitRequest {
  manifest_ids: Array<number>;
  /** Number of splitted manifests */
  splits: number;
}

export interface IManifestDeleteRequest {
  ManifestIds: Array<number>;
  /** Flag: delete linked orders */
  DeleteOrders: boolean;
}

export interface IManifestsToVehicleResponse {
  /** Total manifests with vehicles */
  tot_qty: Array<number>;
  /** Total sended manifests */
  tot_sent: boolean;
}

export interface IManifestNotifyRequest {
  ManifestIds: Array<number>;
  SendSMS: boolean;
  SendEmail: boolean;
}

export interface IManifestBasic {
  name: string;
  driverId?: number;
  vehicleId?: number;

  shift_starts_at?: number;
}

export type IManifestNew = IManifestBasic &
  Partial<IAutoallocateRouteBasic> & {
    id: number;
    driverName?: string;
    vehicleName?: string;
    linkedVehicle?: {
      maxLength?: number;
      lat?: number;
      lng?: number;
    };
    orders: Array<IOrderNode>;
    earliestStart?: string;
    latestFinish?: string;
  };

export type IManifestFull = IManifestNew &
  IManifest & {
    /** in meters */
    final_distance: number;
    /** in seconds */
    final_duration: number;
  };

export interface IManifestLinkingRequest {
  manifestId: number;
  driver_id: number;
  vehicleId: number;
}

export interface IManifestLinkingResponse {
  linked_driver: IDriver;
  linked_vehicle: IVehicle;
}

export type IOrderNode = (IOrderCommon &
  IOrderNodesExtendedInfo &
  IAutoallocationNodeInfo &
  IOrderAdditionalInfo &
  IOrderDate &
  IOrderVehicle &
  IOrderSizing &
  IOrderBreakTime) & {
  orderAttributes?: Array<number>;

  IsPickup: numberBoolean;

  earliest_arrival?: timeStamp;

  OnTime?: numberBoolean;

  time_min?: timeStamp;
  time_max?: timeStamp;

  OrderAttributeString?: string;

  isSelected?: boolean;

  IsLocked?: boolean;
  showPadlock?: boolean;

  isPendingUnlink?: boolean;

  ManifestItemTypeId?: EManifestItemType;
};

export interface IOrderBreakTime {
  BreakTimeId?: number | null;
  BreakDurationSeconds?: number;
  BreakStartTime?: Date | null;
}

export type IManifestForAddRequest = IManifestBasic &
  Partial<IAutoallocateRouteBasic> & {
    orders: Array<{ id: number; IsPickup: numberBoolean }>;
    OptimisationAccepted?: boolean;
  };

export interface IManifestsAddResponse {
  lastAddedId: number;
  totalManifests: number;
}

export interface IRouteOptimizationRequest
  extends IRouteAutoallocateOptimizationOptions {
  drivers: Array<IDriverForRouteOptimization>;
  entities: Array<number>;
}

export interface IRouteAutoallocateOptimizationOptions {
  allow_wait_time: number;
  export_to_xls: boolean;
  ignore_order_times: boolean;
  max_calc_time: number;
  max_vehicle_time: number;
  manifest_id?: number;
}

export interface IDriverForRouteOptimization {
  id: number;
  depo: numberBoolean;
  VehicleId: number;
  StartTime?: string;
  FinishTime?: string;
}

export interface IAccountSummary {
  Id: number;
  Name: string;
  FullName: string;
  Parent?: IAccountSummary;
  ActiveState: string;
  DeactivationDate?: timeStamp;
  NumberOfUsers: number;
  NumberOfSubAccounts: number;
}

export interface IAccountDetail {
  Id: number;
  Name: string;
  FullName: string;
  ParentId?: number;
  TopOfLocalHierarchy: boolean;

  APIKey?: string;
  Timezone?: string;
  ABN?: string;
  Address?: string;
  WebSite?: string;
  PhoneNumber?: string;

  Limited: boolean;
  InTrialPeriod: boolean;
  DeactivationDate?: timeStamp;

  Users?: Array<IUserSummary>;
  SubAccounts?: Array<IAccountSummary>;
  Modules?: Array<IAccountModule>;

  ConsignmentNoteId?: number;
  MapServerId?: number;
  CapacityTypeId?: number;
  capacityType: ICapacityType;
  DisableOptimisation: boolean;
}

export interface IAccountModule {
  AccountId: number;
  ModuleId: number;
  ModuleName: string;
  IsAvailable: boolean;
}

export interface IUserModule {
  UserId: number;
  ModuleId: number;
  ModuleName: string;
  IsVisible: boolean;
  IsReadOnly: boolean;
  CanCreateNew: boolean;
}

export interface IUserSummary {
  AccountId: number;
  Id: number;
  FirstName?: string;
  LastName?: string;
  Name?: string;
  UserName: string;
  UserType: string;
  LastLogin?: timeStamp;
  ExpiryDate?: timeStamp;
  MobileNumber?: string;
}

export interface IUserDetail {
  AccountId: number;
  Id: number;
  FirstName?: string;
  LastName?: string;
  Name?: string;
  UserName: string;
  UserType: string;
  CurrentUser?: boolean;
  LastLogin?: timeStamp;
  ExpiryDate?: timeStamp;
  MobileNumber?: string;
  Password?: string;
  Modules?: Array<IUserModule>;
}

export interface IUserType {
  Id: number;
  Name: string;
}
export interface IAccount {
  account_id: number;
  account_name: string;
  users: Array<IUser>;
}

export type IAccountFull = IAccount &
  IAccountInfo & {
    account_apikey: string;
  };

export interface IUser {
  id: number;
  user_name: string;
  last_visit?: timeStamp;
}

export interface IEventMessageData {
  current_idx: number;
  tot_qty: number;
  message: string;
  is_error: boolean;
  data?: string;
  file?: string;
}

export interface IAutoallocateResult {
  result: 0 | 1;
  shift_starts_at: number;
  routes: Array<IAutoallocateRoute>;
}

interface IAutoallocateRouteBasic {
  start_name: string;
  start_lat: number;
  start_lng: number;
  depo_start: boolean;
  finish_name: string;
  end_lat: number;
  end_lng: number;
  depo_end: boolean;
  /** no idea what it is */
  breaks: Array<unknown>;
}

export type IAutoallocateRoute = IAutoallocateRouteBasic & {
  driver_id: number;
  vehicle_id: number;
  route_start_time: number;
  nodes: Array<
    IAutoallocationNodeInfo & {
      node_nr: number;
      order_id?: number;
      delivery?: boolean;
      /** Minimum time from start in seconds */
      time_min?: number;
      /** Maximum time from start in seconds */
      time_max?: number;
    }
  >;
};

interface IAutoallocationNodeInfo {
  /** in seconds */
  travel_time?: number;
  /** in meters */
  travel_dist?: number;
  service_time?: number;
}

export interface IListSettings {
  module: EModuleIDs;
  settings: {
    filter?: string;
    sort?: ISortSettings;
    page?: IPageSettings;
  };
}

export interface ISortSettings {
  sortColumn: string;
  sortDirection: SortDirection;
}

export interface IPageSettings {
  pageCurrent: number;
  pageSize: number;
}

export interface IManifestStatusInfo {
  icon: ECustomIcons;
  color: ETextClasses;
  text: string;
}

export interface IRouteQueryParams {
  fromDashboard?: boolean;
  fromManifest?: boolean;
  manageAllQrs?: boolean;
  ids?: number[];
  manifestId?: number;
  manifestDate?: string;
}

export interface IAddressInfo {
  address: string;
  lat: number;
  lng: number;
}

export interface IServerMessageStreamInput {
  eventSourceLink?: string;
  asyncImportId?: number;
  asyncFinished?: boolean;
  title: string;
  allocateOrderToManifest?: boolean;
}

export interface IApiResponse<Type> {
  status: number;
  message: string;
  errors?: Array<{
    name: string;
    error: string;
  }>;
  data: Type;
}

export type IPhoneNumberValidation = IApiResponse<{
  isValid: boolean;
}>;

export type IAccountNameValidation = IApiResponse<{
  isValid: boolean;
  suggestedAccountName: string;
}>;

export type IVerificationResponse = IApiResponse<{
  APIKey: string;
  AccountId: number;
  AccountName: string;
  OnBoardingCompleted: false;
  UserId: number;
  UserLogin: string;
  role: ERoles;
  locale: string;
}>;

export enum ESafetyItemDefault {
  Basic = 0,
  NotEditable = 1,
}

export interface ISafetyImage {
  Name: string;
  Url: string;
}

export interface ISafetyItemBase {
  Id: number;
  Title: string;
}

export interface ISafetyDialogProps {
  detail: ISafetyItem;
  categories: ISafetyItemsCategory[];
}
export interface ISafetyItemForSelection extends ISafetyItem {
  selected: boolean;
}

export interface CategorizedItems {
  category: string;
  items: ISafetyItemForSelection[];
  isVisible: boolean;
  isSelected?: boolean;
}

export interface ISafetyItem extends ISafetyItemBase {
  Instruction: string;
  picture?: string | ArrayBuffer;

  ImageName?: string;
  IsCritical: boolean;
  IsDisabled?: boolean;
  IsDefault: ESafetyItemDefault;
  LastModified?: timeStamp;
  AccountId?: number;
  ImageUrl?: string | null;
  ImageThumbUrl?: string | null;
  Results?: null;
  CategoryId: number;
  CategoryName?: string;
  IsVisible?: boolean;
}

export interface ISafetyItemsGroup {
  id: number;
  name: string;
  isSelected?: boolean;
  safetyGroupItems: {
    id: number;
    groupId: number;
    safetyItemId: number;
    safetyItemTitle: string;
    isCritical: boolean;
    instruction: string;
  }[];
}

export interface ISafetyItemsCategory {
  Id: number;
  Description: string;
}
export interface ISafetyItemsGroupRequest {
  id?: number;
  name: string;
  safetyGroupItems: {
    safetyItemId: number;
  }[];
}

export interface IFrequency {
  Id: number;
  TypeId: number;
  Name: string;
  Description: string;
}

export interface ISafetyPlanBase {
  Id: number;
  AccountId?: number;
  FrequencyTypeId?: ESafetyInspectionFrequency;
  FrequencyValues: string;
  MassPlanId?: null;
  Title: string;
  IsDisabled: numberBoolean;
  VehiclesCount: number;
  VehicleGroupsCount: number;
  SafetyItemsCount: number;
  Frequency?: IFrequency;
}

export interface ISafetyPlan extends ISafetyPlanBase {
  NotificationEmail?: string;
  NotificationPhone?: string;
  Vehicles: Array<ISafetyVehicleListItem>;
  VehicleGroups: Array<IVehicleGroup>;
  SafetyItems: Array<ISafetyItemBase>;
  DamagePhotoName?: string;
  picture?: string | ArrayBuffer;
  subtitle?: string;
  IsMandatory: numberBoolean;
  StartDeclarationEnabled: boolean;
  StartDeclaration: string;
  EndDeclarationEnabled: boolean;
  EndDeclaration: string;
}

export interface ISafetyPlanToSave extends ISafetyPlanBase {
  NotificationEmail?: string;
  NotificationPhone?: string;
  VehicleList: Array<number>;
  VehicleGroupList: Array<number>;
  SafetyItemList: Array<number>;
  DamagePhotoName?: string;
  picture?: string | ArrayBuffer;
  subtitle?: string;
  IsMandatory: numberBoolean;
}

export interface ISafetyVehicleListItem {
  Id: number;
  Name: string;
  attributes: Array<number>;
  groups: Array<number>;
}

export interface ISafetyPlanToVehicles {
  PlanId: number;
  Title: string;
}

export interface ISafetyInspectionBase {
  Id: number;
  /** repairer ID */
  AllocatedUserId: number;
  AllocatedUserName: string;
  /** use on save */
  AccountId: number;
  StartTime?: timeStamp;
  EndTime?: timeStamp;
  Duration?: number;
  Location?: string;
  Latitude?: number;
  Longitude?: number;
  WeightStatusId?: ESafetyInspectionStatus;
  StatusId: ESafetyInspectionStatus;
  FinalStatusId: ESafetyInspectionStatus;
  StatusText?: string;
  FinalStatusText?: string;
  WeightResultsStatusText?: string;
  Load?: number;
  Odometer?: number;
  PerformedBy?: string;
  PlanTitle?: string;
  SignatureFileName?: string;
  VehicleName?: string;
  VehicleId?: number;
  PlanId?: number;
  CommentForRepairer?: string;
  RepairerAnswer?: string;
  CrucialInspection?: CrucialInspectionInfo;
  OverDue?: numberBoolean;
}

export interface IInspectionResult {
  Id: number;
  StartTime?: timeStamp;
  EndTime?: timeStamp;
  Duration?: number;
  Notes?: Array<ISafetyNote>;
  SafetyStatusId: ESafetyInspectionStatus;
  SafetyStatusText: string;
  ItemImageName?: string;
  ItemInstruction?: string;
  ItemIsCritical: numberBoolean;
  ItemTitle: string;
}

export interface ISafetyNote {
  Id?: number;
  Images?: Array<string>;
  Note?: string;
  NotedBy?: string;
  TimeStamp?: timeStamp;
}

export interface ISafetyInspection extends ISafetyInspectionBase {
  InspectionResults: Array<IInspectionResult>;
}

export interface ISafetyRepairer {
  Id: number;
  IsRepairer: boolean;
  UserName: string;
}

export interface IOrdersFilters {
  statuses: Array<EOrderStatuses>;
  types: Array<EOrderTypes>;
  zones: Array<number>;
  hideAllocatedOrders: boolean;
  fromDate: Moment;
  toDate: Moment;
  start?: number;
  length?: number;
  sortColumn?: string;
  sortDirection?: string;
  searchText?: string;
  fromDateRange?: Moment | null;
  toDateRange?: Moment | null;
  isSearchActive?: boolean;
}

export interface IFile {
  Name: string;
  Url: string;
}

export interface DateRange {
  fromDate: Moment;
  toDate: Moment;
}

export interface IBaseExportParams {
  offset: number;
  limit: number;
  search?: string;
  showAll?: boolean;
}

export interface IVehicleExportParams extends IBaseExportParams {
  outOfService: boolean;
}

export interface IQrCodeDialogData {
  vehicle: IVehicle;
}

export interface IBarcode {
  id?: number;
  barcode: string;
  orderId?: number;
  isScannedOn?: boolean;
  scannedOnDate?: string;
  isScannedOff?: boolean;
  scannedOffDate?: string | null;
}

export interface IOrderSetting {
  Id: number;
  IsSet: boolean | null;
  OrderId: number;
  SettingId: number;
  SettingName: string;
  SettingType: EOrderSettingTypes;
  SettingTypeName: string;
}

export interface IPageMeta {
  count: number;
  total: number;
}

export interface IListData<T> {
  meta: IPageMeta;
  data: T[];
}

export interface MapBounds {
  northEast: L.LatLng;
  southWest: L.LatLng;
}

export interface MapTilerFeature {
  geometry: { coordinates: number[] };
  place_name_en: string;
}

export interface MapTilerFeatureCollection {
  attribution: string;
  features: MapTilerFeature[];
  query: string[];
  type: string;
}

export interface CompanyInfo {
  address: string;
  location: L.LatLng | null;
}

export interface ISortParams {
  orderBy?: string;
  orderDirection?: string;
}

export interface IPagingParams {
  offset: number;
  limit: number;
}

export interface IManifestQueryParamsModel {
  dateFrom?: string | null;
  dateTo?: string | null;
  search?: string | null;
  orderBy?: string | null;
  orderDirection?: string | null;
  offset?: number | null;
  limit?: number | null;
}

export interface ITableSettings<T> {
  filters: T;
  sorting: ISortParams;
  paging: IPagingParams;
}

export interface IImportHistory<T = null> {
  id: number;
  accountId: number;
  typeId: number;
  typeName: string;
  statusId: number;
  statusName: string;
  startDate: timeStamp;
  endDate: timeStamp;
  details: T[];
  importedEntities: number;
  totalEntities: number;
}

export interface IImportHistoryDetails {
  addDate: timeStamp;
  id: number;
  importHistoryId: number;
  text: string;
  typeId: number;
  typeName: string;
}

export interface LoadTypeDto {
  unit: IdNameDto;
  status: IdNameDto;
}

export interface IdNameDto {
  id: number;
  name: string;
}

export interface IChatboxApiSource {
  title: string;
  url: string;
}

export interface IChatboxApiData {
  answer: string;
  sources?: IChatboxApiSource[];
}

export interface IChatboxApiResponse {
  status: number;
  message: string;
  data: IChatboxApiData;
}

export interface IChatboxAgentResponse {
  status: number;
  message: string;
  data: {
    ai_response: string;
    results: any;
    requires_confirmation: boolean;
    available_functions: string[];
    pending_action: any;
    manifest_data: any;
    order_data: any;
    confirmation_response: any;
  };
  errors?: any;
}

export interface IChatMessage {
  content: string;
  additional_kwargs: { [key: string]: any };
  response_metadata: { [key: string]: any };
  type: string;
  name: string | null;
  id: string | null;
  example: boolean;
  tool_calls?: any[];
  invalid_tool_calls?: any[];
  usage_metadata?: any;
}

export interface IPlanningUpsertRequest {
  manifestName: string;
  manifestId?: number;
  manifestDate: Date;
  driverId?: number;
  vehicleId?: number;
  orders: IPlanningUpsertOrders[];
  isNewOrderAllocated: boolean;
  earliestStart?: string;
  latestFinish?: string;
  returnManifest: boolean;
}

export interface IPlanningUpsertOrders {
  orderId?: number;
  previousManifestId?: number;
  isLocked: boolean;
  breakTimeId?: number;
  shipmentId?: number;
}

export type IManifestNode = ILiveMapManifest & {
  isSelected?: boolean;
  number?: number;
};

export interface PlanSavingConfirmationListModel {
  number: number;
  manifestName: string;
  status: number;
  errors?: string[];
}

export interface IShipmentItemModel {
  Reference: string;
  Product: string;
  CustomDescription: string;
  Quantity: number;
  ItemPrice: number;
  ItemLength: number;
  ItemWidth: number;
  ItemHeight: number;
  ItemWeight: number;
  TotalVolume: number;
  TotalWeight: number;
  ReturnQuantity: number;
  ContainerType: string;
  ClientName: string;
  Unit: string;
  ExtendedPrice: number;
  ItemVolume: number;
  ItemSerials: any[];
  ReturnItemsReasonDescription?: string;
}

export interface IShipmentOrderModel {
  Id: number;
  TypeId: number;
  StatusId: number;
  InvoiceNr: string;
  OrderNumber: string;
  CustomerReference: string;
  CarrierName: string;
  ChargeToCode: string;
  OrderDetails: string;
  AddInfo: string;
  VehicleId: number;
  TrailerId: number;
  AccountId: number;
  dest_address: string;
  dest_lat: number;
  dest_lng: number;
  DeliveryClientID: number;
  DeliveryTime: timeStamp;
  DeliveryTolerance: number;
  DeliveryServiceTime: number;
  DeliveryEnrouteTime: timeStamp;
  DeliveryStartedAt: timeStamp;
  DeliveredTime: timeStamp;
  DeliveryFinishedAt: timeStamp;
  DeliveryComment: string;
  DeliverySignedBy: string;
  delivery_signature_lat: number;
  delivery_signature_lng: number;
  delivery_not_emails: string;
  delivery_not_sms: string;
  delivery_instructions: string;
  pickup_address: string;
  pickup_lat: number;
  pickup_lng: number;
  PickupClientID: number;
  PickupTime: timeStamp;
  PickupTolerance: number;
  PickupServiceTime: number;
  PickupEnrouteTime: timeStamp;
  PickupStartedAt: timeStamp;
  PickedUpTime: timeStamp;
  PickupFinishedAt: timeStamp;
  PickupComment: string;
  PickupSignedBy: string;
  pickup_signature_lat: number;
  pickup_signature_lng: number;
  pickup_not_emails: string;
  pickup_not_sms: string;
  delivery_price: number;
  amount_payed: number;
  payment_type: number;
  money_received: boolean;
  PayerID: number;
  bankdropped: boolean;
  driver_id: number;
  CreationSource: string;
  DateCreated: timeStamp;
  LastModified: timeStamp;
  invoiceLink: string;
  tracking_key: string;
  UID: string;
  CancelledAt: timeStamp;
  CancellationComment: string;
  Futile: boolean;
  IdStr: string;
  ShipmentId: number;
  DeliveryKey: string;
  PickupKey: string;
  ConsPDFFileName: string;
  weight: number;
  volume: number;
  Length: number;
  FragileGoods: numberBoolean;
  DangerousGoods: numberBoolean;
  IsReturn: numberBoolean;
  TopPriority: numberBoolean;
  OnBoard: numberBoolean;
  ShopifyOrderId: string;
  WooCommerceOrderId: string;
  OrderMentumOrderId: string;
  PepperId: string;
  Items: IShipmentItemModel[];
  Attributes: any[];
  LoadUnits: any[];
}

export interface IShipmentModel {
  Id: number;
  AccountId: number;
  CreatedAt: timeStamp;
  UpdatedAt: timeStamp;
  ConsPDFFileName: string;
  DeliveryKey: string;
  PickupKey: string;
  QueueSyncDate: timeStamp;
  DeviceSyncTimeStamp: timeStamp;
  DeviceSyncStatus: string;
  IdStr: string;
  Type: number;
  Orders: IShipmentOrderModel[];
  ShipmentItems: IShipmentItemModel[];
  ShipmentAttributes: any[];
  ShipmentLoadUnits: IShipmentLoadUnit[];
  TypeId: number;
  StatusId: number;
  CreationSource: string;
  DateCreated: timeStamp;
  LastModified: timeStamp;
  invoiceLink: string;
  IsPickup: numberBoolean;
  driver_id: number;
  PayerID: number;
  amount_payed: number;
  payment_type: number;
  money_received: boolean;
  CancelledAt: timeStamp;
  CancellationComment: string;
  bankdropped: boolean;
  UID: string;
  tracking_key: string;
  dest_address: string;
  dest_lat: number;
  dest_lng: number;
  DeliveryClientID: number;
  DeliveryTime: timeStamp;
  DeliveryTolerance: number;
  DeliveryServiceTime: number;
  DeliveryEnrouteTime: timeStamp;
  DeliveryStartedAt: timeStamp;
  DeliveredTime: timeStamp;
  DeliveryFinishedAt: timeStamp;
  DeliveryComment: string;
  DeliverySignedBy: string;
  DeliverySignature: string;
  delivery_signature_lat: number;
  delivery_signature_lng: number;
  delivery_not_emails: string;
  delivery_not_sms: string;
  delivery_instructions: string;
  pickup_address: string;
  pickup_lat: number;
  pickup_lng: number;
  PickupClientID: number;
  PickupTime: timeStamp;
  PickupTolerance: number;
  PickupServiceTime: number;
  PickupEnrouteTime: timeStamp;
  PickupStartedAt: timeStamp;
  PickedUpTime: timeStamp;
  PickupFinishedAt: timeStamp;
  PickupComment: string;
  PickupSignedBy: string;
  PickupSignature: string;
  pickup_signature_lat: number;
  pickup_signature_lng: number;
  pickup_not_emails: string;
  pickup_not_sms: string;
  Weight: number;
  Volume: number;
  Length: number;
  DeliveryPrice: number;
  OnBoard: numberBoolean;
  InvoiceNr: string;
  CustomerReference: string;
  OrderNumber: string;
  CarrierName: string;
  ChargeToCode: string;
  OrderDetails: string;
  AddInfo: string;
  FragileGoods: boolean;
  DangerousGoods: boolean;
  IsReturn: boolean;
  TopPriority: boolean;
  IsFutiled: boolean;
  PepperOrders: boolean;
  WooCommerceOrders: boolean;
  OrderMentumOrders: boolean;
  ShopifyOrders: boolean;
  HasComments: number;
  HasPictures: boolean;
  HasReturns: number;
  HasPDF: boolean;
  DeliveryTrackingLink?: string;
  PickupTrackingLink?: string;
  ManifestId?: number;
  ManifestName?: string;
  ShipmentPayer?: string;
  ShipmentDeliveryCustomer?: string;
  ShipmentPickupCustomer?: string;
  ShipmentBarcodes?: IBarcode[];
}

export interface IShipmentLoadUnit {
  UnitId: number;
  UnitStatus: number;
  Quantity: number;
}

export interface IShipmentSaveModel {
  StatusId?: number;
  CancelledAt?: timeStamp;
  DeliveryTime?: timeStamp;
  DeliveredTime?: timeStamp;
  DeliveryStartedAt?: timeStamp;
  DeliveryFinishedAt?: timeStamp;
  DeliveryEnrouteTime?: timeStamp;
  DeliveryComment?: string;
  DeliverySignedBy?: string;
  DeliverySignature?: number;
  delivery_signature_lat?: number;
  delivery_signature_lng?: number;
  DeliveryTolerance?: number;
  DeliveryServiceTime?: number;
  PickupTime?: timeStamp;
  PickedUpTime?: timeStamp;
  PickupStartedAt?: timeStamp;
  PickupFinishedAt?: timeStamp;
  PickupEnrouteTime?: timeStamp;
  PickupComment?: string;
  PickupSignedBy?: string;
  PickupSignature?: number;
  pickup_signature_lat?: number;
  pickup_signature_lng?: number;
  PickupTolerance?: number;
  PickupServiceTime?: number;
}
